import { useEffect, useState } from 'react';

import { createAbortError } from './abortError';

let idleCallbackTimeout: ReturnType<typeof requestIdleCallback> | undefined;
const pendingIdleCallbacks = new Map<object, () => void>();

function flushCleanup() {
  for (const callback of pendingIdleCallbacks.values()) {
    try {
      callback();
    } catch (error) {
      reportError(error);
    }
  }
  idleCallbackTimeout = undefined;
  pendingIdleCallbacks.clear();
}

function scheduleCleanup(value: object, callback: () => void) {
  pendingIdleCallbacks.set(value, callback);
  idleCallbackTimeout ??= requestIdleCallback(flushCleanup);
}

function cancelCleanup(value: object) {
  pendingIdleCallbacks.delete(value);
  if (pendingIdleCallbacks.size === 0 && idleCallbackTimeout !== undefined) {
    cancelIdleCallback(idleCallbackTimeout);
    idleCallbackTimeout = undefined;
  }
}

function createAbortController() {
  return new AbortController();
}

/** Returns a signal that aborts when the component unmounts */
export default function useAbortSignal(): AbortSignal {
  const [abortController, setAbortController] = useState(createAbortController);

  useEffect(() => {
    // Cancel pending cleanup
    cancelCleanup(abortController);

    return () => {
      // Defer cleanup
      scheduleCleanup(abortController, () => {
        abortController.abort(
          createAbortError('signal is aborted due to the component unmounting'),
        );
        setAbortController(createAbortController);
      });
    };
  }, [abortController]);

  return abortController.signal;
}
